import React, { useState, useEffect, useCallback } from "react";
import Layout from "../../Layout";
import { useNavigate } from "react-router-dom";
import { BiChevronLeft, BiChevronDown, BiPlus, BiTrash } from "react-icons/bi";
import { toast } from "react-hot-toast";
import axios from "axios";
import { BaseUrl } from "../../BaseUrl.js";

function CreateOrder() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [searchPatient, setSearchPatient] = useState("");
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);

  // Order form state
  const [medications, setMedications] = useState([
    {
      name: "",
      dosage: "",
      frequency: "1-1-1",
      duration: "30 Days",
      remarks: "",
      price: 0,
    },
  ]);
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("Pay on Delivery");

  // Fetch patients
  const fetchPatients = useCallback(async () => {
    try {
      const response = await axios.get(`${BaseUrl}/doctor/patients/filter`, {
        withCredentials: true,
      });
      const data = response.data?.data || response.data || [];
      setPatients(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching patients:", error);
    }
  }, []);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  // Filter patients based on search
  const filteredPatients = patients.filter((p) =>
    (p.title || p.fullName || "")
      .toLowerCase()
      .includes(searchPatient.toLowerCase())
  );

  // Add medication row
  const addMedication = () => {
    setMedications([
      ...medications,
      {
        name: "",
        dosage: "",
        frequency: "1-1-1",
        duration: "30 Days",
        remarks: "",
        price: 0,
      },
    ]);
  };

  // Remove medication row
  const removeMedication = (index) => {
    if (medications.length > 1) {
      setMedications(medications.filter((_, i) => i !== index));
    }
  };

  // Update medication field
  const updateMedication = (index, field, value) => {
    const updated = [...medications];
    updated[index][field] = value;
    setMedications(updated);
  };

  // Calculate total
  const total = medications.reduce(
    (sum, med) => sum + (parseFloat(med.price) || 0),
    0
  );

  // Submit order
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedPatient) {
      toast.error("Please select a patient");
      return;
    }

    if (medications.every((m) => !m.name.trim())) {
      toast.error("Please add at least one medication");
      return;
    }

    setLoading(true);

    try {
      // Create prescription which auto-creates order
      // Prescription model expects medications as array of strings
      // MedicalRecord (order) will convert to objects in the controller
      const medicationNames = medications
        .filter((m) => m.name.trim())
        .map((m) => m.name);

      const response = await axios.post(
        `${BaseUrl}/doctor/prescription/create`,
        {
          patientId: selectedPatient._id,
          patientName: selectedPatient.title || selectedPatient.fullName,
          medications: medicationNames,
          notes: medications.map((m) => `${m.name}: ${m.remarks}`).join("; "),
          symptoms: [], // Can be empty array
          diagnosis: "", // Must be string, not array
          // Order-specific fields
          deliveryAddress: deliveryAddress,
          totalAmount: total,
          paymentMethod: paymentMethod,
          // Send medication details for price calculation
          medicationDetails: medications
            .filter((m) => m.name.trim())
            .map((m) => ({
              name: m.name,
              composition: m.dosage || "",
              frequency: m.frequency || "1-1-1",
              duration: m.duration || "30 Days",
              instruction: m.remarks || "",
              instruction: m.remarks || "",
              price: parseFloat(m.price) || 0,
            })),
        },
        { withCredentials: true }
      );

      console.log("Order created:", response.data);
      toast.success("Order created successfully!");
      navigate("/orders");
    } catch (error) {
      console.error("Error creating order:", error);
      toast.error(error.response?.data?.message || "Failed to create order");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      {/* Header */}
      <div className='mb-6 flex items-center gap-4'>
        <button
          onClick={() => navigate("/orders")}
          className='flex h-10 w-10 items-center justify-center rounded-lg border border-border hover:bg-dry'
        >
          <BiChevronLeft className='text-xl' />
        </button>
        <h1 className='text-xl font-semibold'>Create New Order</h1>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Patient Selection */}
        <div className='mb-6 rounded-xl border border-border bg-white p-6'>
          <h2 className='mb-4 text-lg font-semibold'>Patient Details</h2>

          <div className='relative'>
            <label className='mb-2 block text-sm font-medium text-textGray'>
              Select Patient
            </label>
            <div
              className='flex h-14 cursor-pointer items-center justify-between rounded-lg border border-border bg-dry px-4'
              onClick={() => setShowPatientDropdown(!showPatientDropdown)}
            >
              <span className={selectedPatient ? "text-main" : "text-textGray"}>
                {selectedPatient
                  ? selectedPatient.title || selectedPatient.fullName
                  : "Search or select patient..."}
              </span>
              <BiChevronDown className='text-xl text-textGray' />
            </div>

            {showPatientDropdown && (
              <div className='absolute z-10 mt-2 max-h-60 w-full overflow-hidden rounded-lg border border-border bg-white shadow-lg'>
                <input
                  type='text'
                  placeholder='Search patient...'
                  value={searchPatient}
                  onChange={(e) => setSearchPatient(e.target.value)}
                  className='w-full border-b border-border px-4 py-3 text-sm focus:outline-none'
                  onClick={(e) => e.stopPropagation()}
                />
                <div className='max-h-48 overflow-y-auto'>
                  {filteredPatients.length > 0 ? (
                    filteredPatients.map((patient) => (
                      <div
                        key={patient._id}
                        className='flex cursor-pointer items-center gap-3 px-4 py-3 hover:bg-dry'
                        onClick={() => {
                          setSelectedPatient(patient);
                          setDeliveryAddress(patient.address || "");
                          setShowPatientDropdown(false);
                          setSearchPatient("");
                        }}
                      >
                        <div className='flex h-8 w-8 items-center justify-center rounded-full bg-[#0095D9] text-sm text-white'>
                          {(patient.title ||
                            patient.fullName ||
                            "?")[0].toUpperCase()}
                        </div>
                        <span className='text-sm'>
                          {patient.title || patient.fullName}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div className='px-4 py-3 text-sm text-textGray'>
                      No patients found
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {selectedPatient && (
            <div className='mt-4'>
              <label className='mb-2 block text-sm font-medium text-textGray'>
                Delivery Address
              </label>
              <textarea
                value={deliveryAddress}
                onChange={(e) => setDeliveryAddress(e.target.value)}
                placeholder='Enter delivery address...'
                className='h-24 w-full resize-none rounded-lg border border-border bg-dry px-4 py-3 text-sm focus:border-[#0095D9] focus:outline-none'
              />
            </div>
          )}
        </div>

        {/* Medications */}
        <div className='mb-6 rounded-xl border border-border bg-white p-6'>
          <div className='mb-4 flex items-center justify-between'>
            <h2 className='text-lg font-semibold'>Medications</h2>
            <button
              type='button'
              onClick={addMedication}
              className='flex items-center gap-2 rounded-lg bg-[#0095D9] px-4 py-2 text-sm text-white'
            >
              <BiPlus /> Add Item
            </button>
          </div>

          <div className='overflow-x-auto'>
            <table className='w-full'>
              <thead className='border-b border-border'>
                <tr>
                  <th className='pb-3 text-left text-xs font-semibold text-textGray'>
                    #
                  </th>
                  <th className='pb-3 text-left text-xs font-semibold text-textGray'>
                    Medicine Name
                  </th>
                  <th className='pb-3 text-left text-xs font-semibold text-textGray'>
                    Dosage
                  </th>
                  <th className='pb-3 text-left text-xs font-semibold text-textGray'>
                    Frequency
                  </th>
                  <th className='pb-3 text-left text-xs font-semibold text-textGray'>
                    Duration
                  </th>
                  <th className='pb-3 text-left text-xs font-semibold text-textGray'>
                    Remarks
                  </th>
                  <th className='pb-3 text-right text-xs font-semibold text-textGray'>
                    Price (₹)
                  </th>
                  <th className='pb-3 text-center text-xs font-semibold text-textGray'>
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {medications.map((med, index) => (
                  <tr
                    key={index}
                    className='border-b border-border last:border-0'
                  >
                    <td className='py-3 text-sm'>{index + 1}</td>
                    <td className='py-3'>
                      <input
                        type='text'
                        value={med.name}
                        onChange={(e) =>
                          updateMedication(index, "name", e.target.value)
                        }
                        placeholder='Enter medicine'
                        className='w-full rounded border border-border px-3 py-2 text-sm focus:border-[#0095D9] focus:outline-none'
                      />
                    </td>
                    <td className='py-3'>
                      <input
                        type='text'
                        value={med.dosage}
                        onChange={(e) =>
                          updateMedication(index, "dosage", e.target.value)
                        }
                        placeholder='e.g., 500mg'
                        className='w-24 rounded border border-border px-3 py-2 text-sm focus:border-[#0095D9] focus:outline-none'
                      />
                    </td>
                    <td className='py-3'>
                      <input
                        type='text'
                        value={med.frequency}
                        onChange={(e) =>
                          updateMedication(index, "frequency", e.target.value)
                        }
                        placeholder='1-1-1'
                        className='w-20 rounded border border-border px-3 py-2 text-sm focus:border-[#0095D9] focus:outline-none'
                      />
                    </td>
                    <td className='py-3'>
                      <input
                        type='text'
                        value={med.duration}
                        onChange={(e) =>
                          updateMedication(index, "duration", e.target.value)
                        }
                        placeholder='30 Days'
                        className='w-24 rounded border border-border px-3 py-2 text-sm focus:border-subMain focus:outline-none'
                      />
                    </td>
                    <td className='py-3'>
                      <input
                        type='text'
                        value={med.remarks}
                        onChange={(e) =>
                          updateMedication(index, "remarks", e.target.value)
                        }
                        placeholder='Notes'
                        className='w-full rounded border border-border px-3 py-2 text-sm focus:border-[#0095D9] focus:outline-none'
                      />
                    </td>
                    <td className='py-3 text-right'>
                      <input
                        type='number'
                        value={med.price}
                        onChange={(e) =>
                          updateMedication(index, "price", e.target.value)
                        }
                        placeholder='0'
                        className='w-20 rounded border border-border px-3 py-2 text-right text-sm focus:border-[#0095D9] focus:outline-none'
                      />
                    </td>
                    <td className='py-3 text-center'>
                      <button
                        type='button'
                        onClick={() => removeMedication(index)}
                        className='text-red-500 hover:text-red-700 disabled:text-gray-300'
                        disabled={medications.length === 1}
                      >
                        <BiTrash className='text-lg' />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Total */}
          <div className='mt-4 flex justify-end border-t border-border pt-4'>
            <div className='text-right'>
              <span className='text-sm text-textGray'>Total: </span>
              <span className='text-xl font-bold text-[#0095D9]'>
                ₹{total.toFixed(2)}
              </span>
            </div>
          </div>
        </div>

        {/* Payment Method */}
        <div className='mb-6 rounded-xl border border-border bg-white p-6'>
          <h2 className='mb-4 text-lg font-semibold'>Payment Method</h2>
          <div className='flex gap-4'>
            {["Pay on Delivery", "Online Payment", "Cash"].map((method) => (
              <label
                key={method}
                className={`flex cursor-pointer items-center gap-2 rounded-lg border px-4 py-3 transition ${
                  paymentMethod === method
                    ? "border-[#0095D9] bg-[#0095D9] bg-opacity-10"
                    : "border-border hover:bg-dry"
                }`}
              >
                <input
                  type='radio'
                  name='paymentMethod'
                  value={method}
                  checked={paymentMethod === method}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className='accent-[#0095D9]'
                />
                <span className='text-sm'>{method}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Submit Button */}
        <div className='flex justify-end gap-4 py-6'>
          <button
            type='button'
            onClick={() => navigate("/orders")}
            className='rounded-lg border border-border px-8 py-3 text-main hover:bg-dry'
          >
            Cancel
          </button>
          <button
            type='submit'
            disabled={loading}
            className='rounded-lg bg-[#0095D9] px-8 py-3 text-white hover:bg-opacity-90 disabled:opacity-50'
          >
            {loading ? "Creating..." : "Create Order"}
          </button>
        </div>
      </form>
    </Layout>
  );
}

export default CreateOrder;
